import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { Search, Plus, Trash2, FileText, History, Calendar, User, Hash, Warehouse, Building2, AlertCircle, Eye, X } from 'lucide-react';
import DataTable from 'react-data-table-component';

export default function ComprasPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('new'); // 'new' | 'history'

    // Data State
    const [providers, setProviders] = useState([]);
    const [products, setProducts] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [bodegas, setBodegas] = useState([]);
    const [filteredBodegas, setFilteredBodegas] = useState([]);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showPurchaseDetail, setShowPurchaseDetail] = useState(false);

    // Form State
    const [header, setHeader] = useState({
        proveedor_id: '',
        numero_factura: '',
        fecha_compra: new Date().toISOString().split('T')[0],
        sucursal_id: '',
        bodega_id: ''
    });
    const [cart, setCart] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [precioTipo, setPrecioTipo] = useState('bruto'); // 'bruto' (con IVA) | 'neto' (sin IVA)
    const IVA_RATE = 0.19; // 19% IVA Chile

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('❌ No hay token de autenticación');
            return;
        }
        const headers = { 'Authorization': `Bearer ${token}` };

        // Load Providers
        fetch(`${API_URL}/api/providers`, { headers })
            .then(async res => {
                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({ error: res.statusText }));
                    // If unauthorized, redirect to login
                    if (res.status === 401 || res.status === 400) {
                        console.warn('⚠️ Token inválido o expirado, redirigiendo al login...');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        navigate('/login');
                        return;
                    }
                    throw new Error(errorData.error || `Error ${res.status}: ${res.statusText}`);
                }
                return res.json();
            })
            .then(data => {
                if (data && Array.isArray(data)) {
                    setProviders(data);
                } else {
                    console.error('❌ Respuesta de proveedores no es un array:', data);
                    setProviders([]);
                }
            })
            .catch(err => {
                console.error('❌ Error cargando proveedores:', err);
                setProviders([]);
            });

        // Load History
        fetch(`${API_URL}/api/purchases`, { headers })
            .then(async res => {
                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({ error: res.statusText }));
                    // If unauthorized, redirect to login
                    if (res.status === 401 || res.status === 400) {
                        console.warn('⚠️ Token inválido o expirado, redirigiendo al login...');
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        navigate('/login');
                        return;
                    }
                    throw new Error(errorData.error || `Error ${res.status}: ${res.statusText}`);
                }
                return res.json();
            })
            .then(data => {
                if (data && Array.isArray(data)) {
                    setPurchases(data);
                } else {
                    console.error('❌ Respuesta de compras no es un array:', data);
                    setPurchases([]);
                }
            })
            .catch(err => {
                console.error('❌ Error cargando compras:', err);
                setPurchases([]);
            });

        // Load Sucursales & Bodegas
        Promise.all([
            fetch(`${API_URL}/api/sucursales`, { headers })
                .then(res => {
                    if (!res.ok) {
                        throw new Error(`Error ${res.status}: ${res.statusText}`);
                    }
                    return res.json();
                })
                .then(data => {
                    console.log('✅ Sucursales cargadas:', data);
                    // Show all sucursales, not just active ones
                    const sucursalesList = (data || []).map(s => ({
                        ...s,
                        id: s.id || s.sucursal_id,
                        nombre: s.nombre || 'Sin nombre'
                    }));
                    setSucursales(sucursalesList);
                    if (sucursalesList.length === 0) {
                        console.warn('⚠️ No hay sucursales disponibles');
                    }
                })
                .catch(err => {
                    console.error('❌ Error cargando sucursales:', err);
                    setSucursales([]);
                }),
            fetch(`${API_URL}/api/bodegas`, { headers })
                .then(res => {
                    if (!res.ok) {
                        throw new Error(`Error ${res.status}: ${res.statusText}`);
                    }
                    return res.json();
                })
                .then(data => {
                    console.log('✅ Bodegas cargadas (raw):', data);
                    // Normalize bodega data to ensure consistent id format
                    const normalizedBodegas = (data || []).map(b => ({
                        ...b,
                        id: b.id || b.bodega_id,
                        sucursal_id: b.sucursal_id || null
                    }));
                    
                    // Remove duplicates: keep only one bodega per (nombre + sucursal_id) combination
                    const uniqueBodegas = [];
                    const seen = new Set();
                    
                    normalizedBodegas.forEach(b => {
                        const key = `${b.sucursal_id || 'null'}_${b.nombre}`;
                        if (!seen.has(key)) {
                            seen.add(key);
                            uniqueBodegas.push(b);
                        }
                    });
                    
                    console.log('✅ Bodegas únicas:', uniqueBodegas);
                    setBodegas(uniqueBodegas);
                })
                .catch(err => {
                    console.error('❌ Error cargando bodegas:', err);
                    setBodegas([]);
                })
        ]).catch(err => {
            console.error('Error en carga inicial:', err);
        });
    }, []);

    // Select initial sucursal after both sucursales and bodegas are loaded (only once)
    const [initialSucursalSelected, setInitialSucursalSelected] = useState(false);
    
    useEffect(() => {
        if (initialSucursalSelected) return; // Only run once
        
        if (sucursales.length > 0 && bodegas.length > 0) {
            // Find sucursales that have bodegas
            const sucursalesConBodegas = sucursales.filter(s => {
                const sucursalId = s.id || s.sucursal_id;
                return bodegas.some(b => {
                    const bSucursalId = typeof b.sucursal_id === 'string' ? parseInt(b.sucursal_id) : b.sucursal_id;
                    return bSucursalId === sucursalId;
                });
            });
            
            if (sucursalesConBodegas.length > 0) {
                // Prefer active sucursal with bodegas, otherwise first one with bodegas
                const activeSucursal = sucursalesConBodegas.find(s => s.estado === true) || sucursalesConBodegas[0];
                const firstId = activeSucursal.id || activeSucursal.sucursal_id;
                console.log('Seleccionando sucursal inicial (con bodegas):', { id: firstId, nombre: activeSucursal.nombre });
                setHeader(prev => ({ ...prev, sucursal_id: firstId.toString() }));
                setInitialSucursalSelected(true);
            } else if (sucursales.length > 0) {
                // No sucursales have bodegas, select first active or first one
                const activeSucursal = sucursales.find(s => s.estado === true) || sucursales[0];
                const firstId = activeSucursal.id || activeSucursal.sucursal_id;
                console.log('Seleccionando sucursal inicial (sin bodegas):', { id: firstId, nombre: activeSucursal.nombre });
                setHeader(prev => ({ ...prev, sucursal_id: firstId.toString() }));
                setInitialSucursalSelected(true);
            }
        } else if (sucursales.length > 0 && bodegas.length === 0) {
            // If no bodegas loaded yet, select first active sucursal
            const activeSucursal = sucursales.find(s => s.estado === true) || sucursales[0];
            const firstId = activeSucursal.id || activeSucursal.sucursal_id;
            console.log('Seleccionando sucursal inicial (sin bodegas cargadas):', { id: firstId, nombre: activeSucursal.nombre });
            setHeader(prev => ({ ...prev, sucursal_id: firstId.toString() }));
            setInitialSucursalSelected(true);
        }
    }, [sucursales, bodegas, initialSucursalSelected]);

    // Search products when searchTerm changes (debounced)
    useEffect(() => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setProducts([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        const timeoutId = setTimeout(() => {
            fetch(`${API_URL}/api/products?search=${encodeURIComponent(searchTerm.trim())}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            })
                .then(res => res.json())
                .then(data => {
                    setProducts(data);
                    setIsSearching(false);
                })
                .catch(err => {
                    console.error(err);
                    setIsSearching(false);
                });
        }, 300); // Debounce 300ms

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Filter bodegas when sucursal changes
    useEffect(() => {
        if (!header.sucursal_id) {
            setFilteredBodegas([]);
            setHeader(prev => ({ ...prev, bodega_id: '' }));
            return;
        }

        if (bodegas.length === 0) {
            setFilteredBodegas([]);
            return;
        }

        const sucursalIdNum = parseInt(header.sucursal_id);
        if (isNaN(sucursalIdNum)) {
            console.warn('⚠️ Invalid sucursal_id:', header.sucursal_id);
            setFilteredBodegas([]);
            return;
        }

        const filtered = bodegas.filter(b => {
            // Normalize sucursal_id to number for comparison
            let bSucursalId = b.sucursal_id;
            if (typeof bSucursalId === 'string') {
                bSucursalId = parseInt(bSucursalId);
            }
            if (bSucursalId === null || bSucursalId === undefined) {
                return false;
            }
            return bSucursalId === sucursalIdNum;
        });
        
        console.log(`🔍 Filtrando bodegas para sucursal ${sucursalIdNum}:`, {
            totalBodegas: bodegas.length,
            filtered: filtered.length,
            sucursalSeleccionada: header.sucursal_id,
            todasLasBodegas: bodegas.map(b => ({ 
                id: b.id || b.bodega_id, 
                nombre: b.nombre, 
                sucursal_id: b.sucursal_id,
                tipo_sucursal_id: typeof b.sucursal_id
            })),
            bodegasFiltradas: filtered.map(b => ({ 
                id: b.id || b.bodega_id, 
                nombre: b.nombre, 
                sucursal_id: b.sucursal_id 
            }))
        });
        
        setFilteredBodegas(filtered);
        if (filtered.length > 0) {
            // Use the first bodega's ID (handle both id formats)
            const bodegaId = (filtered[0].id || filtered[0].bodega_id)?.toString() || '';
            setHeader(prev => {
                // Only update if different to avoid infinite loops
                if (prev.bodega_id !== bodegaId) {
                    return { ...prev, bodega_id: bodegaId };
                }
                return prev;
            });
        } else {
            setHeader(prev => ({ ...prev, bodega_id: '' }));
        }
    }, [header.sucursal_id, bodegas]);

    const addToCart = (product) => {
        setCart(prev => {
            const exists = prev.find(p => p.id === product.id);
            if (exists) return prev; // Already in cart, use modify
            // Default cost to current buy price or 0
            return [...prev, {
                id: product.id,
                nombre: product.nombre,
                quantity: 1,
                cost: product.valor_compra || 0,
                fecha_expiracion: '', // Campo opcional para fecha de expiración
                requiere_expiracion: false // Checkbox para indicar si requiere fecha
            }];
        });
        setSearchTerm(''); // Clear search to continue working
    };

    const updateCartItem = (id, field, value) => {
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                // Para campos numéricos, parsear; para otros, usar el valor directamente
                if (field === 'quantity' || field === 'cost') {
                    return { ...item, [field]: parseFloat(value) || 0 };
                } else if (field === 'requiere_expiracion') {
                    return { ...item, [field]: value, fecha_expiracion: value ? item.fecha_expiracion : '' };
                } else {
                    return { ...item, [field]: value };
                }
            }
            return item;
        }));
    };

    const removeCartItem = (id) => setCart(prev => prev.filter(p => p.id !== id));

    // Calculate totals based on price type
    const calculateTotals = () => {
        const sumaCostos = cart.reduce((sum, item) => sum + (item.quantity * item.cost), 0);
        
        if (precioTipo === 'bruto') {
            // Bruto: precio ya incluye IVA
            const neto = Math.round(sumaCostos / (1 + IVA_RATE));
            const iva = sumaCostos - neto;
            return { neto, iva, total: sumaCostos };
        } else {
            // Neto: precio sin IVA, hay que agregarlo
            const iva = Math.round(sumaCostos * IVA_RATE);
            const total = sumaCostos + iva;
            return { neto: sumaCostos, iva, total };
        }
    };

    const calculateTotal = () => calculateTotals().total;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (cart.length === 0) return alert('Agregue productos a la compra');
        if (!header.proveedor_id) return alert('Seleccione un proveedor');
        if (!header.bodega_id) return alert('Seleccione una bodega');

        const totals = calculateTotals();
        
        try {
            const res = await fetch(`${API_URL}/api/purchases`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    proveedor_id: header.proveedor_id,
                    numero_factura: header.numero_factura,
                    fecha_compra: header.fecha_compra,
                    bodega_id: parseInt(header.bodega_id),
                    items: cart,
                    precio_tipo: precioTipo,
                    monto_neto: totals.neto,
                    monto_iva: totals.iva,
                    total: totals.total
                })
            });

            if (res.ok) {
                alert('Compra registrada exitosamente');
                setCart([]);
                setHeader(prev => ({ ...prev, numero_factura: '' }));
                // Reload history
                const histRes = await fetch(`${API_URL}/api/purchases`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
                setPurchases(await histRes.json());
                setActiveTab('history');
            } else {
                alert('Error al registrar compra');
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Products are already filtered by backend search
    const filteredProducts = products;

    const fetchPurchaseDetail = useCallback(async (id) => {
        if (id == null || id === '') return;
        try {
            const res = await fetch(`${API_URL}/api/purchases/${id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedPurchase(data);
                setShowPurchaseDetail(true);
            } else if (res.status === 404) {
                alert('Compra no encontrada');
            } else {
                const errData = await res.json().catch(() => ({}));
                alert(errData.error || 'Error al cargar detalle');
            }
        } catch (err) {
            console.error(err);
            alert('Error al cargar detalle');
        }
    }, []);

    const handleRowClick = useCallback((row) => {
        if (row?.id != null) fetchPurchaseDetail(row.id);
    }, [fetchPurchaseDetail]);

    const handleVerDetalle = useCallback((e, id) => {
        e?.stopPropagation?.();
        if (id != null) fetchPurchaseDetail(id);
    }, [fetchPurchaseDetail]);

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Gestión de Compras</h1>

                    {/* Tabs */}
                    <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 inline-flex">
                        <button
                            onClick={() => setActiveTab('new')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'new' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center gap-2">
                                <Plus size={16} />
                                <span>Nueva Compra</span>
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'history' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                        >
                            <div className="flex items-center gap-2">
                                <History size={16} />
                                <span>Historial</span>
                            </div>
                        </button>
                    </div>
                </div>

                {activeTab === 'new' ? (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        {/* LEFT COLUMN: Main Form Container (2 cols) */}
                        <div className="xl:col-span-2 space-y-6">

                            {/* Card principal: formulario de compra */}
                            <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden ring-1 ring-gray-100/80 border-t-4 border-t-indigo-500">

                                {/* Section 1: Invoice Header */}
                                <div className="p-6 border-b border-gray-100 bg-gray-50/30">
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
                                            <FileText size={20} />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">Información de la Factura</h3>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">Proveedor</label>
                                            <div className="relative group">
                                                <User className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                </div>
                                                <select
                                                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all appearance-none font-medium text-gray-700"
                                                    value={header.proveedor_id}
                                                    onChange={e => setHeader({ ...header, proveedor_id: e.target.value })}
                                                >
                                                    <option value="">Seleccionar Proveedor...</option>
                                                    {providers.map(p => (
                                                        <option key={p.proveedor_id} value={p.proveedor_id}>{p.nombre}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">N° Factura</label>
                                            <div className="relative group">
                                                <Hash className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                <input
                                                    type="text"
                                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all font-medium text-gray-700"
                                                    placeholder="Ej: 001-23456"
                                                    value={header.numero_factura}
                                                    onChange={e => setHeader({ ...header, numero_factura: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">Fecha Emisión</label>
                                            <div className="relative group">
                                                <Calendar className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                <input
                                                    type="date"
                                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all font-medium text-gray-700"
                                                    value={header.fecha_compra}
                                                    onChange={e => setHeader({ ...header, fecha_compra: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Row 2: Sucursal & Bodega */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">Sucursal</label>
                                            <div className="relative group">
                                                <Building2 className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                </div>
                                                <select
                                                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all appearance-none font-medium text-gray-700"
                                                    value={header.sucursal_id}
                                                    onChange={e => setHeader({ ...header, sucursal_id: e.target.value })}
                                                    disabled={sucursales.length === 0}
                                                >
                                                    {sucursales.length === 0 ? (
                                                        <option value="">Cargando sucursales...</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Seleccionar Sucursal...</option>
                                                            {sucursales.map((s, index) => {
                                                                const sucursalId = s.id || s.sucursal_id;
                                                                const uniqueKey = `sucursal_${sucursalId}_${index}`;
                                                                return (
                                                                    <option key={uniqueKey} value={sucursalId}>
                                                                        {s.nombre}
                                                                    </option>
                                                                );
                                                            })}
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide ml-1">Bodega Destino</label>
                                            <div className="relative group">
                                                <Warehouse className="absolute left-3 top-3 text-gray-400 group-focus-within:text-orange-500 transition-colors" size={18} />
                                                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                </div>
                                                <select
                                                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all appearance-none font-medium text-gray-700"
                                                    value={header.bodega_id}
                                                    onChange={e => setHeader({ ...header, bodega_id: e.target.value })}
                                                    disabled={!header.sucursal_id || filteredBodegas.length === 0}
                                                >
                                                    {!header.sucursal_id ? (
                                                        <option value="">Seleccione primero una sucursal</option>
                                                    ) : filteredBodegas.length === 0 ? (
                                                        <option value="">No hay bodegas en esta sucursal</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Seleccionar Bodega...</option>
                                                            {filteredBodegas.map((b, index) => {
                                                                const bodegaId = b.id || b.bodega_id;
                                                                const uniqueKey = `${bodegaId}_${b.sucursal_id}_${index}`;
                                                                return (
                                                                    <option key={uniqueKey} value={bodegaId}>
                                                                        {b.nombre}
                                                                    </option>
                                                                );
                                                            })}
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Products List (Cart) */}
                                <div className="p-6 bg-white flex flex-col min-h-[400px] border-b border-gray-100">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                                        <h3 className="text-lg font-bold text-gray-800">Detalle de Productos</h3>
                                        <div className="flex items-center gap-4">
                                            {/* Tipo de Precio Toggle */}
                                            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                                <button
                                                    onClick={() => setPrecioTipo('bruto')}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                                        precioTipo === 'bruto'
                                                            ? 'bg-white text-indigo-600 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                    }`}
                                                >
                                                    Bruto (con IVA)
                                                </button>
                                                <button
                                                    onClick={() => setPrecioTipo('neto')}
                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                                        precioTipo === 'neto'
                                                            ? 'bg-white text-indigo-600 shadow-sm'
                                                            : 'text-gray-500 hover:text-gray-700'
                                                    }`}
                                                >
                                                    Neto (sin IVA)
                                                </button>
                                            </div>
                                            <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
                                                {cart.length} productos
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex-grow border rounded-xl overflow-hidden border-gray-100">
                                        <div className="overflow-x-auto">
                                            {cart.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center p-12 text-center bg-gray-50/50 h-full">
                                                    <div className="bg-white p-4 rounded-full shadow-sm mb-3">
                                                        <Search className="text-indigo-200" size={32} />
                                                    </div>
                                                    <p className="text-gray-500 font-medium">No hay productos en la orden</p>
                                                    <p className="text-sm text-gray-400 mt-1">Busque productos en el panel derecho para comenzar</p>
                                                </div>
                                            ) : (
                                                <table className="w-full">
                                                    <thead className="bg-gray-50/80 border-b border-gray-100">
                                                        <tr>
                                                            <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-[30%]">Producto</th>
                                                            <th className="text-center py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-[10%]">Cant.</th>
                                                            <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-[20%]">Costo</th>
                                                            <th className="text-center py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-[25%]">Fecha Expiración</th>
                                                            <th className="text-right py-3 px-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-[15%]">Total</th>
                                                            <th className="w-10"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {cart.map(item => (
                                                            <tr key={`cart-${item.id}`} className={`hover:bg-indigo-50/30 transition-colors group ${item.requiere_expiracion && item.fecha_expiracion ? 'bg-yellow-50/50' : ''}`}>
                                                                <td className="py-3 px-4">
                                                                    <div className="font-semibold text-gray-700">{item.nombre}</div>
                                                                    <div className="text-xs text-gray-400">ID: {item.id}</div>
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <div className="flex justify-center">
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            className="w-16 text-center bg-white border border-gray-200 rounded-lg py-1 text-sm font-medium focus:ring-2 focus:ring-indigo-100 outline-none"
                                                                            value={item.quantity}
                                                                            onChange={e => updateCartItem(item.id, 'quantity', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <div className="relative">
                                                                        <span className="absolute left-2.5 top-1.5 text-gray-400 text-xs">$</span>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            className="w-full bg-white border border-gray-200 rounded-lg pl-6 pr-2 py-2 text-base font-medium focus:ring-2 focus:ring-indigo-100 outline-none"
                                                                            style={{ minWidth: '120px' }}
                                                                            value={item.cost}
                                                                            onChange={e => updateCartItem(item.id, 'cost', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-4">
                                                                    <div className="flex flex-col gap-2">
                                                                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={item.requiere_expiracion || false}
                                                                                onChange={e => updateCartItem(item.id, 'requiere_expiracion', e.target.checked)}
                                                                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                                            />
                                                                            <span className="flex items-center gap-1">
                                                                                <AlertCircle size={12} />
                                                                                Requiere expiración
                                                                            </span>
                                                                        </label>
                                                                        {item.requiere_expiracion && (
                                                                            <input
                                                                                type="date"
                                                                                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-yellow-100 focus:border-yellow-500 outline-none"
                                                                                value={item.fecha_expiracion || ''}
                                                                                onChange={e => updateCartItem(item.id, 'fecha_expiracion', e.target.value)}
                                                                                min={new Date().toISOString().split('T')[0]}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-4 text-right">
                                                                    <div className="font-bold text-gray-800">
                                                                        ${(item.quantity * item.cost).toLocaleString('es-CL')}
                                                                    </div>
                                                                </td>
                                                                <td className="py-3 px-4 text-right">
                                                                    <button
                                                                        onClick={() => removeCartItem(item.id)}
                                                                        className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 3: Footer Summary & Action */}
                                <div className="bg-gray-50/80 border-t border-gray-200 p-6">
                                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                                        <div className="text-sm text-gray-500">
                                            <p>Complete los datos y agregue productos para finalizar.</p>
                                            <p className="text-xs mt-1 text-indigo-600">
                                                {precioTipo === 'bruto' 
                                                    ? '* Los precios ingresados ya incluyen IVA (19%)'
                                                    : '* El IVA (19%) se agregará al total'
                                                }
                                            </p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 sm:gap-6">
                                            {/* Desglose de precios */}
                                            <div className="bg-white rounded-xl border border-gray-200 p-4 min-w-[200px]">
                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-500">Neto:</span>
                                                        <span className="font-medium text-gray-700">
                                                            ${calculateTotals().neto.toLocaleString('es-CL')}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-500">IVA (19%):</span>
                                                        <span className="font-medium text-gray-700">
                                                            ${calculateTotals().iva.toLocaleString('es-CL')}
                                                        </span>
                                                    </div>
                                                    <div className="border-t border-gray-100 pt-2 flex justify-between">
                                                        <span className="text-gray-800 font-semibold">Total:</span>
                                                        <span className="text-xl font-bold text-indigo-600">
                                                            ${calculateTotals().total.toLocaleString('es-CL')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={cart.length === 0}
                                                className={`px-8 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-95 flex items-center gap-2
                                                    ${cart.length === 0
                                                        ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                                        : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 hover:shadow-indigo-300'
                                                    }`}
                                            >
                                                <FileText size={18} />
                                                <span>Registrar Compra</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Search Panel (1 col) */}
                        <div className="xl:col-span-1">
                            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 sticky top-6 ring-1 ring-gray-100/80">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                    <Search size={20} className="text-indigo-500" />
                                    Catálogo
                                </h3>

                                <div className="space-y-4">
                                    {/* Search Input */}
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Buscar por nombre, código o marca..."
                                            className="w-full pl-14 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder-gray-400 text-gray-700"
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="absolute left-3 top-3.5 text-gray-400 pointer-events-none">
                                            <Search size={18} />
                                        </div>
                                    </div>

                                    {/* Suggestions List - mt-4 separa el input del hint para evitar superposición visual */}
                                    <div className="space-y-2 mt-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-1 customize-scrollbar">
                                        {searchTerm && searchTerm.trim().length >= 2 && filteredProducts.map(p => (
                                            <button
                                                type="button"
                                                key={`product-${p.id}`}
                                                onClick={() => addToCart(p)}
                                                className="w-full text-left p-3 hover:bg-indigo-50 border border-gray-100 hover:border-indigo-200 bg-white rounded-xl flex justify-between items-center group transition-all shadow-sm hover:shadow-md"
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-gray-700 truncate text-sm">{p.nombre}</div>
                                                    {p.marca_nombre && (
                                                        <div className="text-xs text-indigo-600 font-medium">{p.marca_nombre}</div>
                                                    )}
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${p.stock_actual > 10 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                            Stock: {p.stock_actual || 0}
                                                        </span>
                                                        {p.codigo_barra && (
                                                            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                                                                {p.codigo_barra}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="bg-indigo-50 text-indigo-600 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Plus size={16} />
                                                </div>
                                            </button>
                                        ))}

                                        {searchTerm && searchTerm.trim().length >= 2 && isSearching && (
                                            <div className="text-center py-8 px-4 bg-indigo-50 rounded-xl border border-dashed border-indigo-200">
                                                <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                                                <p className="text-indigo-600 font-medium text-sm">Buscando productos...</p>
                                            </div>
                                        )}

                                        {searchTerm && searchTerm.trim().length >= 2 && !isSearching && filteredProducts.length === 0 && (
                                            <div className="text-center py-8 px-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                <p className="text-gray-500 font-medium text-sm">No se encontraron productos</p>
                                                <p className="text-xs text-gray-400 mt-1">Intente con otro término</p>
                                            </div>
                                        )}

                                        {searchTerm && searchTerm.trim().length < 2 && (
                                            <div className="text-center py-8 px-4 bg-yellow-50 rounded-xl border border-dashed border-yellow-200">
                                                <p className="text-yellow-600 font-medium text-sm">Escriba al menos 2 caracteres</p>
                                                <p className="text-xs text-yellow-500 mt-1">para buscar productos</p>
                                            </div>
                                        )}

                                        {!searchTerm && (
                                            <div className="text-center py-12 px-6">
                                                <div className="bg-indigo-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                                                    <Search className="text-indigo-400" size={20} />
                                                </div>
                                                <p className="text-gray-500 font-medium text-sm">Empiece a escribir para buscar</p>
                                                <p className="text-xs text-gray-400 mt-1 max-w-[200px] mx-auto">Busque por nombre, código de barra o marca.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <DataTable
                            columns={[
                                { name: 'ID', selector: row => row.id, sortable: true, width: '70px', cell: row => <span className="font-mono font-bold text-gray-600">#{row.id}</span> },
                                { name: 'Fecha', selector: row => new Date(row.created_at).toLocaleDateString(), sortable: true, width: '100px' },
                                { name: 'Proveedor', selector: row => row.proveedor_name || 'N/A', sortable: true, cell: row => <span className="font-medium text-gray-800">{row.proveedor_name}</span> },
                                { name: 'N° Factura', selector: row => row.numero_factura, sortable: true, width: '110px' },
                                { name: 'Bodega', selector: row => row.bodega_name, sortable: true, cell: row => (
                                    <div className="text-xs">
                                        <div className="font-medium text-orange-700">{row.bodega_name || '-'}</div>
                                        <div className="text-gray-400">{row.sucursal_name || ''}</div>
                                    </div>
                                )},
                                { name: 'Usuario', selector: row => row.usuario_name, sortable: true, cell: row => <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-600">{row.usuario_name}</span> },
                                { name: 'Total', selector: row => row.total, sortable: true, cell: row => <span className="font-bold text-indigo-600 text-base block text-right">${parseInt(row.total).toLocaleString('es-CL')}</span> },
                                { name: 'Acciones', cell: row => (
                                    <button
                                        type="button"
                                        onClick={(e) => handleVerDetalle(e, row.id)}
                                        className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 flex items-center gap-2 text-sm"
                                    >
                                        <Eye size={14} /> Ver Detalle
                                    </button>
                                ), width: '120px' },
                            ]}
                            data={purchases}
                            pagination
                            highlightOnHover
                            pointerOnHover
                            onRowClicked={handleRowClick}
                            customStyles={{
                                headRow: {
                                    style: {
                                        backgroundColor: '#f8fafc',
                                        borderBottom: '1px solid #e2e8f0',
                                        fontWeight: '700',
                                        color: '#64748b',
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        paddingTop: '12px',
                                        paddingBottom: '12px'
                                    },
                                },
                            }}
                        />
                    </div>
                )}

                {/* Purchase Detail Modal */}
                {showPurchaseDetail && selectedPurchase && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Detalle de Compra #{selectedPurchase.id}</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {selectedPurchase.proveedor_name} • {new Date(selectedPurchase.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <button onClick={() => setShowPurchaseDetail(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-xs text-gray-500">N° Factura</p>
                                        <p className="font-semibold text-gray-800">{selectedPurchase.numero_factura}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-xs text-gray-500">Bodega</p>
                                        <p className="font-semibold text-gray-800">{selectedPurchase.bodega_name}</p>
                                    </div>
                                </div>
                                <h3 className="text-lg font-bold mb-4">Productos</h3>
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                                        <tr>
                                            <th className="px-4 py-3">Producto</th>
                                            <th className="px-4 py-3 text-right">Cantidad</th>
                                            <th className="px-4 py-3 text-right">Precio Unit.</th>
                                            <th className="px-4 py-3 text-right">Total</th>
                                            <th className="px-4 py-3">Vencimiento</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {selectedPurchase.items?.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3">
                                                    <div className="font-medium">{item.producto_nombre}</div>
                                                    <div className="text-xs text-gray-500">{item.codigo_barra}</div>
                                                </td>
                                                <td className="px-4 py-3 text-right font-mono">{item.cantidad}</td>
                                                <td className="px-4 py-3 text-right font-mono">${parseInt(item.precio_unitario).toLocaleString('es-CL')}</td>
                                                <td className="px-4 py-3 text-right font-bold">${parseInt(item.precio_total).toLocaleString('es-CL')}</td>
                                                <td className="px-4 py-3">
                                                    {item.fecha_expiracion ? (
                                                        <span className="text-xs text-orange-600">
                                                            {new Date(item.fecha_expiracion).toLocaleDateString()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">N/A</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold text-gray-900 border-t">
                                        <tr>
                                            <td colSpan="3" className="px-4 py-3 text-right">Total</td>
                                            <td className="px-4 py-3 text-right">${parseInt(selectedPurchase.total).toLocaleString('es-CL')}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
